<template>
  <div class="rb-ob-form rb-match-page">
    <template v-if="person">
      <header>
        <div class="rb-ob-progress" :aria-label="$t('intake.progressLabel')">
          <div class="rb-ob-progress-meta">
            <span>{{ progressMeta(9) }}</span>
            <span>{{ $t('intake.stepLabels.profile') }}</span>
          </div>
          <div class="rb-ob-progress-bar" aria-hidden="true">
            <i :style="{ '--p': progressPercent(9) }"></i>
          </div>
        </div>
        <h1 class="rb-ob-title">{{ $t('intake.profileTitle') }}</h1>
      </header>

      <article class="rb-profile-card">
        <div class="rb-profile-hero">
          <span class="rb-avatar rb-avatar--lg" aria-hidden="true">
            <img
                v-if="person.photoUrl && !photoFailed"
                :src="person.photoUrl"
                alt=""
                @error="onPhotoError"
            >
            <template v-else>{{ person.initial }}</template>
          </span>
          <div>
            <span class="rb-profile-eyebrow">{{ personTier }}</span>
            <h2>{{ personName }}</h2>
            <p>{{ personTitle }}</p>
            <div v-if="person.sandbox && /^t\d+$/.test(person.id)" class="rb-profile-tags">
              <span>{{ $t('intake.profileTagAdult') }}</span>
              <span>{{ $t('intake.profileTagAnxiety') }}</span>
              <span>{{ $t('intake.profileTagFa') }}</span>
            </div>
          </div>
        </div>

        <div class="rb-profile-sections">
          <section v-if="person.professional">
            <h3>{{ $route.params.lang === 'en' ? 'Professional profile' : 'اطلاعات حرفه‌ای' }}</h3>
            <dl>
              <template v-for="row in professionalRows"><dt :key="row.label + '-label'">{{ row.label }}</dt><dd :key="row.label + '-value'">{{ row.value }}</dd></template>
            </dl>
          </section>
          <section v-if="aboutBody">
            <h3>{{ $t('intake.profileAboutTitle') }}</h3>
            <p>{{ aboutBody }}</p>
          </section>
          <section>
            <h3>{{ $t('intake.profileWhyTitle') }}</h3>
            <p>{{ personReason }}</p>
          </section>
          <section v-if="person.sandbox && /^t\d+$/.test(person.id)">
            <h3>{{ $t('intake.profileCredentialsTitle') }}</h3>
            <ul>
              <li>{{ $t('intake.profileCredentialLicense') }}</li>
              <li>{{ $t('intake.profileCredentialTopic') }}</li>
              <li>{{ $t('intake.profileCredentialCapacity') }}</li>
            </ul>
          </section>
        </div>

        <div class="rb-profile-sticky">
          <span>
            {{ $t('intake.profilePerSession') }}
            <b>{{ priceLabel }}</b>
          </span>
          <button class="rb-ob-submit" type="button" @click="viewSlots">
            {{ $t('intake.profileViewSlots') }}
          </button>
        </div>
      </article>
    </template>
  </div>
</template>

<script>
import {
  loadIntake,
  saveIntake,
  hasSafetyRisk,
  isIntakeReadyToMatch,
  toFaDigits,
} from '@/intake/intakeState'
import {formatMoneyForDisplay, priceTierLabel} from '@/intake/formatMoney'
import {getMockMatch} from '@/intake/mockMatches'
import {isSandboxMatchEnabled} from '@/intake/sandboxMatchApi'
import {getSessionPerson, hasSandboxMatchSession} from '@/intake/sandboxMatchSession'

export default {
  name: 'MatchProfileStep',
  data() {
    return {
      person: null,
      photoFailed: false,
    }
  },
  computed: {
    professionalRows() {
      const p = (this.person && this.person.professional) || {}
      const fa = this.$route.params.lang !== 'en'
      const missing = fa ? 'اعلام نشده' : 'Not provided'
      const list = (v) => Array.isArray(v) && v.length ? v.join('، ') : missing
      return [
        {label: fa ? 'تخصص' : 'Specialty', value: ({individual:fa?'فردی':'Individual',couple:fa?'زوج‌درمانی':'Couples',family:fa?'خانواده':'Family',child:fa?'کودک':'Child',teenager:fa?'نوجوان':'Adolescent',psychiatry:fa?'روان‌پزشکی':'Psychiatry',couple_individual:fa?'فردی و زوج‌درمانی':'Individual and couples'})[p.specialty] || p.specialty || missing},
        {label: fa ? 'سابقهٔ کار' : 'Experience', value: p.experience == null ? missing : String(p.experience) + (fa ? ' سال' : ' years')},
        {label: fa ? 'رویکردها' : 'Approaches', value: list(p.approaches)},
        {label: fa ? 'گروه‌های مراجع' : 'Populations', value: list(p.populations)},
        {label: fa ? 'زبان‌ها' : 'Languages', value: list(p.languages)},
      ]
    },
    personName() {
      if (!this.person) {
        return ''
      }
      if (this.person.name) {
        return this.person.name
      }
      return this.$t('intake.therapist.' + this.person.id + '.name')
    },
    personTitle() {
      if (!this.person) {
        return ''
      }
      if (this.person.title && !/^tier_\d+$/i.test(this.person.title)) {
        return this.person.title
      }
      if (this.person.sandbox && /^t\d+$/i.test(String(this.person.id))) {
        return this.$t('intake.therapist.' + this.person.id + '.title')
      }
      return ''
    },
    personTier() {
      if (!this.person) {
        return ''
      }
      if (this.person.tier) {
        return priceTierLabel(this.person.tier, this.$route.params.lang || 'fa')
      }
      if (this.person.sandbox && /^t\d+$/i.test(String(this.person.id))) {
        return this.$t('intake.therapist.' + this.person.id + '.tier')
      }
      return ''
    },
    personReason() {
      if (!this.person) {
        return ''
      }
      if (this.person.reason) {
        return this.person.reason
      }
      if (this.person.explanation_rendered_fa) {
        return this.person.explanation_rendered_fa
      }
      return this.person.sandbox && /^t\d+$/.test(this.person.id) ? this.$t('intake.therapist.' + this.person.id + '.reason') : ''
    },
    aboutBody() {
      if (this.person && this.person.bio) {
        return this.person.bio
      }
      return this.person && this.person.sandbox && /^t\d+$/.test(this.person.id) ? this.$t('intake.profileAboutBody') : ''
    },
    priceLabel() {
      if (!this.person) {
        return ''
      }
      const money = formatMoneyForDisplay(this.person.price, this.person.currency)
      if (money.isToman || money.unit === 'raw') {
        return money.text + ' ' + this.$t('intake.toman')
      }
      if (money.currency) {
        return money.text + ' ' + money.currency
      }
      return money.text
    },
  },
  created() {
    const draft = loadIntake()
    // V2 screening match: session people are authoritative — do not bounce to legacy draft gates.
    const hasV2Match = isSandboxMatchEnabled() && hasSandboxMatchSession()
    if (!hasV2Match) {
      if (!isIntakeReadyToMatch(draft)) {
        this.$router.replace({name: 'intakeScreening'})
        return
      }
      if (hasSafetyRisk(draft.safety)) {
        this.$router.replace({name: 'intakeCrisis'})
        return
      }
    }

    const id = this.$route.params.therapistId
    let person = null
    if (hasV2Match) {
      person = getSessionPerson(id)
    } else {
      person = getMockMatch(id)
    }
    if (!person) {
      this.$router.replace({name: 'intakeMatches'})
      return
    }

    this.person = person
    saveIntake({therapistId: person.id})
  },
  methods: {
    progressPercent() { return '67%' },
    toFaDigits,
    onPhotoError() {
      this.photoFailed = true
    },
    progressMeta() {
      return this.$t('intake.progressMeta', {
        step: this.toFaDigits(2),
        total: this.toFaDigits(3),
      })
    },
    viewSlots() {
      if (!this.person) {
        return
      }
      saveIntake({therapistId: this.person.id})
      this.$router.push({
        name: 'intakeSlots',
        params: {
          lang: this.$route.params.lang || 'fa',
          therapistId: this.person.id,
        },
      })
    },
  },
}
</script>
